import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faCheckCircle, faEnvelope, faExclamationTriangle, faInfoCircle, faSms, faTimes } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { useToast } from '@/hooks/useToast';
import { useAbsenceStore } from '../features/absences/store/useAbsenceStore';
import { AppCreateAbsenceDto } from '@/api/generated';
import { buildCancellationMessage, cancelBooking as apiCancelBooking, notifyCustomer } from '@/features/bookings/hooks/useBookingActions';

interface AbsenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (absenceData: any) => void;
  staffList: Array<{ id: string; name: string }>;
  existingBookings?: Array<{
    id: string;
    start: string;
    end: string;
    customer: { firstName: string; lastName: string; email: string; phone: string };
    worker: { displayName: string };
    serviceName: string;
  }>;
}

interface ConflictBooking {
  id: string;
  start: string;
  end: string;
  customer: { firstName: string; lastName: string; email: string; phone: string };
  worker: { displayName: string };
  serviceName: string;
  willBeCancelled: boolean;
  notificationSent: boolean;
}

const AbsenceModal: React.FC<AbsenceModalProps> = ({ isOpen, onClose, onSuccess, staffList, existingBookings = [] }) => {
  const toast = useToast();
  const { createAbsence } = useAbsenceStore();

  const [formData, setFormData] = useState({
    title: '',
    type: 'vacation',
    selectedStaff: 'all',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    startTime: '09:00',
    endTime: '17:00',
    description: '',
    notifyCustomers: true,
    notificationMethod: 'email', // email, sms, both
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [conflictBookings, setConflictBookings] = useState<ConflictBooking[]>([]);
  const [showConflictWarning, setShowConflictWarning] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        type: 'vacation',
        selectedStaff: 'all',
        startDate: dayjs().format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        startTime: '09:00',
        endTime: '17:00',
        description: '',
        notifyCustomers: true,
        notificationMethod: 'email',
      });
      setConflictBookings([]);
      setShowConflictWarning(false);
    }
  }, [isOpen]);

  // Check for conflicts when form data changes
  useEffect(() => {
    if (formData.startDate && formData.endDate && formData.startTime && formData.endTime) {
      checkForConflicts();
    }
  }, [formData.startDate, formData.endDate, formData.startTime, formData.endTime, formData.selectedStaff]);

  const checkForConflicts = () => {
    const startDateTime = dayjs(`${formData.startDate} ${formData.startTime}`);
    const endDateTime = dayjs(`${formData.endDate} ${formData.endTime}`);

    const conflicts = existingBookings.filter(booking => {
      const bookingStart = dayjs(booking.start);
      const bookingEnd = dayjs(booking.end);

      // Check if booking overlaps with blocked time
      const overlaps = bookingStart.isBefore(endDateTime) && bookingEnd.isAfter(startDateTime);

      // Check if staff matches (if not "all")
      const staffMatches = formData.selectedStaff === 'all' || booking.worker.displayName === staffList.find(s => s.id === formData.selectedStaff)?.name;

      return overlaps && staffMatches;
    });

    const conflictBookingsWithStatus = conflicts.map(booking => ({
      ...booking,
      willBeCancelled: true,
      notificationSent: false,
    }));

    setConflictBookings(conflictBookingsWithStatus);
    setShowConflictWarning(conflictBookingsWithStatus.length > 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleToggleCancellation = (bookingId: string) => {
    setConflictBookings(prev => prev.map(booking => (booking.id === bookingId ? { ...booking, willBeCancelled: !booking.willBeCancelled } : booking)));
  };

  const sendNotificationToCustomer = async (booking: ConflictBooking) => {
    try {
      const message = buildCancellationMessage({
        customerFirstName: booking.customer.firstName,
        customerLastName: booking.customer.lastName,
        bookingStart: booking.start,
        blockTitle: formData.title,
        reason: formData.description || undefined,
      });
      await notifyCustomer({
        bookingId: booking.id,
        subject: `Zrušení rezervace — ${formData.title}`,
        message,
        channel: formData.notificationMethod as 'email' | 'sms' | 'both',
      });
      return true;
    } catch (error) {
      console.error('Chyba při odesílání notifikace:', error);
      return false;
    }
  };

  const matchedWaitingTotal: { id: string; customerName: string }[] = [];

  const cancelBooking = async (bookingId: string) => {
    try {
      const res = await apiCancelBooking(bookingId, `Blokovaný čas: ${formData.title}`);
      res.matchingWaitingList.forEach(m => matchedWaitingTotal.push({ id: m.id, customerName: m.customerName }));
      return true;
    } catch (error) {
      console.error('Chyba při zrušení rezervace:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (conflictBookings.some(b => b.willBeCancelled) && formData.notifyCustomers) {
      const confirmed = window.confirm(`Budou zrušeny ${conflictBookings.filter(b => b.willBeCancelled).length} rezervací a odeslány notifikace zákazníkům. Pokračovat?`);
      if (!confirmed) return;
    }

    setIsSubmitting(true);

    try {
      // 1. Vytvoření absence (blokovaného času)
      const createAbsenceRequest: AppCreateAbsenceDto = {
        name: formData.title,
        type: formData.type,
        userId: formData.selectedStaff === 'all' ? undefined : formData.selectedStaff, // undefined = všichni zaměstnanci
        startDate: formData.startDate,
        startTime: formData.startTime,
        endDate: formData.endDate,
        endTime: formData.endTime,
        description: formData.description,
      };

      const absenceData = await createAbsence(createAbsenceRequest);

      // 2. Zrušení konfliktních rezervací
      const bookingsToCancel = conflictBookings.filter(b => b.willBeCancelled);

      for (const booking of bookingsToCancel) {
        const cancelled = await cancelBooking(booking.id);
        if (cancelled) {
          // 3. Odeslání notifikací zákazníkům
          if (formData.notifyCustomers) {
            const notificationSent = await sendNotificationToCustomer(booking);
            if (notificationSent) {
              setConflictBookings(prev => prev.map(b => (b.id === booking.id ? { ...b, notificationSent: true } : b)));
            }
          }
        }
      }

      toast.success(
        'Absence byla vytvořena',
        `Absence "${formData.title}" byla úspěšně vytvořena${bookingsToCancel.length > 0 ? ` a ${bookingsToCancel.length} rezervací bylo zrušeno` : ''}.`,
      );

      if (matchedWaitingTotal.length > 0) {
        const uniq = Array.from(new Map(matchedWaitingTotal.map(m => [m.id, m])).values());
        const names = uniq.slice(0, 3).map(m => m.customerName).join(', ');
        toast.info(
          '🔔 Čekací listina',
          `${uniq.length} klientů čeká na podobný slot${names ? `: ${names}${uniq.length > 3 ? '…' : ''}` : ''}. Otevři Čekací listinu.`,
          7000,
        );
      }

      if (onSuccess) {
        onSuccess(absenceData);
      }

      onClose();
    } catch (error) {
      console.error('Chyba při vytváření blokovaného času:', error);
      toast.error('Chyba', 'Nepodařilo se vytvořit blokovaný čas. Zkuste to prosím znovu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeOptions = () => [
    { value: 'vacation', label: 'Dovolená', color: 'bg-blue-100 text-blue-800' },
    { value: 'training', label: 'Školení', color: 'bg-purple-100 text-purple-800' },
    { value: 'maintenance', label: 'Údržba', color: 'bg-orange-100 text-orange-800' },
    { value: 'personal', label: 'Osobní', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'holiday', label: 'Svátky', color: 'bg-red-100 text-red-800' },
    { value: 'other', label: 'Jiné', color: 'bg-gray-100 text-gray-800' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBan} className="text-red-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Nová absence</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Základní informace */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Název *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                placeholder="Např. Dovolená - Anna Svobodová"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Typ blokování *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              >
                {getTypeOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Zaměstnanec */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zaměstnanec *</label>
            <select
              name="selectedStaff"
              value={formData.selectedStaff}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            >
              <option value="all">Všichni zaměstnanci</option>
              {staffList.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name}
                </option>
              ))}
            </select>
          </div>

          {/* Datum a čas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Datum začátku *</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Čas začátku *</label>
              <input
                type="time"
                name="startTime"
                value={formData.startTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Datum konce *</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Čas konce *</label>
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
            </div>
          </div>

          {/* Popis */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Popis</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              placeholder="Dodatečné informace o blokovaném čase..."
            />
          </div>

          {/* Konfliktní rezervace */}
          {showConflictWarning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mr-2" />
                <h3 className="text-sm font-medium text-yellow-800">Nalezeny konfliktní rezervace</h3>
              </div>

              <div className="space-y-3">
                {conflictBookings.map(booking => (
                  <div key={booking.id} className="bg-white border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={booking.willBeCancelled}
                            onChange={() => handleToggleCancellation(booking.id)}
                            className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                          />
                          <span className="text-sm font-medium text-gray-900">
                            {booking.customer.firstName} {booking.customer.lastName}
                          </span>
                          <span className="text-xs text-gray-500">{dayjs(booking.start).format('DD.MM.YYYY HH:mm')}</span>
                          <span className="text-xs text-gray-500">{booking.worker.displayName}</span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {booking.serviceName} • {booking.customer.email} • {booking.customer.phone}
                        </div>
                      </div>
                      {booking.notificationSent && <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notifikace zákazníků */}
          {conflictBookings.some(b => b.willBeCancelled) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mr-2" />
                <h3 className="text-sm font-medium text-blue-800">Notifikace zákazníků</h3>
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="notifyCustomers"
                    checked={formData.notifyCustomers}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Odeslat notifikace zákazníkům o zrušených rezervacích</span>
                </label>

                {formData.notifyCustomers && (
                  <div className="ml-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Způsob notifikace</label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationMethod"
                          value="email"
                          checked={formData.notificationMethod === 'email'}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <FontAwesomeIcon icon={faEnvelope} className="ml-2 text-gray-600" />
                        <span className="ml-1 text-sm text-gray-700">E-mail</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationMethod"
                          value="sms"
                          checked={formData.notificationMethod === 'sms'}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <FontAwesomeIcon icon={faSms} className="ml-2 text-gray-600" />
                        <span className="ml-1 text-sm text-gray-700">SMS</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="notificationMethod"
                          value="both"
                          checked={formData.notificationMethod === 'both'}
                          onChange={handleInputChange}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-1 text-sm text-gray-700">Oboje</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Akce */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Zrušit
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Vytváření...' : 'Vytvořit absenci'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AbsenceModal;
